import { initialStepOrder, Step } from '@/features/flyer/configs/steps'
import { mockedFlyerTemplate } from '@/features/flyer/mocks/flyer-template'
import { Bundle } from '@/features/sitecore/actions/services/sitecore-bundle'
import { TranslationBundles } from '@/features/sitecore/configs/translation-bundles'
import SitecoreService from '@/features/sitecore/sitecore-service'
import { FlyerTemplate, FlyerType, Orientation } from '../model/templates'

export interface Destination {
	label: string
	area?: string
	region?: string
	port?: string
	images?: string[]
	imagesLandscape?: string[]
	headline: string
	copy: string
}

/**
 * Retrieve a list of destinations from the Sitecore bundle `TranslationBundles.BUILD_YOUR_FLYER_DESTINATIONS`.
 *
 * @returns {Destination[]} An array of destinations with their label, area, region, and checked status.
 */
export function getDestinationsFromSitecore(destinationsBundle: Bundle): Destination[] {
	const destinationCount = Object.keys(destinationsBundle).filter((key) => key.match(/DESTINATION_(\d*)_LABEL/) != null).length

	const destinations: Destination[] = []
	for (let i = 0; i < destinationCount; i += 1) {
		const label = destinationsBundle[`DESTINATION_${i}_LABEL`]
		const area = destinationsBundle[`DESTINATION_${i}_AREA`]
		const region = destinationsBundle[`DESTINATION_${i}_REGION`]
		const port = destinationsBundle[`DESTINATION_${i}_PORT`]
		const images = destinationsBundle[`DESTINATION_${i}_IMAGES`]
		const imagesLandscape = destinationsBundle[`DESTINATION_${i}_IMAGES_LANDSCAPE`]
		const headline = destinationsBundle[`DESTINATION_${i}_HEADLINE`]
		const copy = destinationsBundle[`DESTINATION_${i}_COPY`]

		destinations.push({
			label,
			area: area || undefined, // Empty string is falsy
			region: region || undefined,
			port: port || undefined,
			images: images?.split(',') || [],
			imagesLandscape: imagesLandscape?.split(',') || [],
			headline: headline || '',
			copy: copy || '',
		})
	}

	return destinations
}

export interface Offer {
	priceNames: string[]
	promotionImages: string[]
	heroImage: string | undefined
	termsAndConditions: string
	overwritePriceName: string
}

/*
 * Retrieve the offers from the Sitecore bundle for each priceName provided.
 *
 * @returns {Object} An object with an offer for each priceName.
 */
export function escapeRegExSpecialChar(string: string) {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function getOffersFromSitecore(offersBundle: Bundle, priceNames: string[]): { [key: string]: Offer } {
	const priceNameGroups = Object.keys(offersBundle).filter((key) => key.match(/MSC-BUILD_YOUR_FLYER-OFFER_\d+_PRICENAMES/) != null)
	const defaultOffer = mapOffer(offersBundle, 0)
	const offers: { [key: string]: Offer } = {}
	priceNames.forEach((priceName) => {
		const escapedPriceName = escapeRegExSpecialChar(priceName)
		const foundPriceNamesKey = priceNameGroups.find((key) => offersBundle[key].toUpperCase().match(`${escapedPriceName.toUpperCase()}(\\||$)`) != null)
		const index = foundPriceNamesKey ? +(foundPriceNamesKey.match(/MSC-BUILD_YOUR_FLYER-OFFER_(\d)_PRICENAMES/)?.[1] || '0') : 0
		offers[priceName] = index === 0 ? defaultOffer : mapOffer(offersBundle, index)
	})

	return offers
}

function mapOffer(bundle: Bundle, i: number): Offer {
	const priceNames = bundle[`MSC-BUILD_YOUR_FLYER-OFFER_${i}_PRICENAMES`]
	const promotionImages = bundle[`MSC-BUILD_YOUR_FLYER-OFFER_${i}_IMAGES`]
	const heroImage = bundle[`MSC-BUILD_YOUR_FLYER-OFFER_${i}_HERO`]
	const termsAndConditions = bundle[`MSC-BUILD_YOUR_FLYER-OFFER_${i}_TC`]
	const overwritePriceName = bundle[`MSC-BUILD_YOUR_FLYER-OFFER_${i}_OVERWRITE_PRICE_NAMES`]

	return {
		priceNames: priceNames?.split('|') || [],
		promotionImages: promotionImages && promotionImages?.trim() !== '' ? promotionImages.split('|') : [],
		heroImage: heroImage || undefined,
		termsAndConditions: termsAndConditions || '',
		overwritePriceName: overwritePriceName || '',
	}
}

export async function getFlyerTemplateFromSitecore(templateBundle: TranslationBundles, flyerType: FlyerType, orientation: Orientation): Promise<FlyerTemplate[]> {
	const bundleTemplate = await SitecoreService.getBundle(templateBundle)
	const templateCount = Object.keys(bundleTemplate).filter((key) => key.match(new RegExp(`.*FLYER-TEMPLATE-${orientation.toUpperCase()}-([0-9]+$)`)) != null).length

	const flyerTemplates: FlyerTemplate[] = []
	for (let i = 0; i < templateCount; i += 1) {
		const description = await SitecoreService.getLabel(templateBundle, `MSC-BUILD_YOUR_FLYER-${flyerType.toUpperCase()}-FLYER-DESCRIPTION-${i}`, 'template')
		const template = await SitecoreService.getLabel(templateBundle, `MSC-BUILD_YOUR_FLYER-${flyerType.toUpperCase()}-FLYER-TEMPLATE-${orientation.toUpperCase()}-${i}`, mockedFlyerTemplate)
		const isSelectable = await SitecoreService.getBoolean(templateBundle, `MSC-BUILD_YOUR_FLYER-${flyerType.toUpperCase()}-FLYER-SELECTABLE-${i}`, true)

		flyerTemplates.push({ template, description, isSelectable, orientation, flyerType, identifier: i.toString() })
	}

	return flyerTemplates
}

export async function getStepOrderFiltered(flyerType: FlyerType): Promise<Step[]> {
	const flyerTemplate = await SitecoreService.getBundle(TranslationBundles.BUILD_YOUR_FLYER)
	return initialStepOrder[flyerType].filter((step) => flyerTemplate[`MSC-BUILD_YOUR_FLYER-SKIP-STEP-${step.toUpperCase()}`] !== 'true')
}

/*return Object.keys(stepOrder).reduce((acc, key) => {
	const stepKey = key as FlyerType
	acc[stepKey] = stepOrder[stepKey].filter((step: string) => flyerTemplate[`MSC-BUILD_YOUR_FLYER-SKIP-STEP-${step.toUpperCase()}`] !== 'true')

	return acc
}, {} as StepOrder)*/
